import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tattooStyles = [
  'Tradicional', 'Neo-Tradicional', 'Realismo', 'Blackwork', 
  'Dotwork', 'Japonés', 'Tribal', 'Minimalista', 
  'Acuarela', 'Geométrico', 'Chicano', 'Lettering'
];

const RequestQuoteButton = ({ artistId, artistName }) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    body_part: '',
    size: '',
    style: ''
  });

  if (user?.user_type !== 'client' || user?.id === artistId) {
    return null;
  }

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error(language === 'es' ? 'Describe tu idea de tatuaje' : 'Describe your tattoo idea');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        artist_id: artistId,
        ...formData,
        reference_images: []
      });
      
      setDialogOpen(false);
      setFormData({ description: '', body_part: '', size: '', style: '' });
      toast.success(language === 'es' ? '¡Solicitud enviada! El artista te responderá con una cotización.' : 'Request sent! The artist will respond with a quote.');
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar la solicitud' : 'Error sending request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent uppercase tracking-widest text-xs"
          data-testid="request-quote-btn"
        >
          <FileText size={16} className="mr-2" />
          {t('profile.requestQuote')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Cinzel'] text-xl">
            {t('quotes.requestQuote')} - {artistName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-zinc-500">
            {language === 'es' 
              ? 'Describe tu idea y el artista te enviará una cotización con precio estimado.'
              : 'Describe your idea and the artist will send you a quote with estimated price.'}
          </p>
          
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.describeIdea')} *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('quotes.descriptionPlaceholder')}
              className="bg-transparent border-zinc-800 mt-2 min-h-[120px]"
              data-testid="quote-description"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.bodyPart')}</Label>
            <Input
              value={formData.body_part}
              onChange={(e) => setFormData(prev => ({ ...prev, body_part: e.target.value }))}
              placeholder={t('quotes.bodyPartPlaceholder')}
              className="bg-transparent border-zinc-800 mt-2"
              data-testid="quote-body-part"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.approximateSize')}</Label>
            <Input
              value={formData.size}
              onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
              placeholder={t('quotes.sizePlaceholder')}
              className="bg-transparent border-zinc-800 mt-2"
              data-testid="quote-size"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.preferredStyle')}</Label>
            <Select
              value={formData.style}
              onValueChange={(value) => setFormData(prev => ({ ...prev, style: value }))}
            >
              <SelectTrigger className="bg-transparent border-zinc-800 mt-2" data-testid="quote-style">
                <SelectValue placeholder={t('search.allStyles')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {tattooStyles.map((style) => (
                  <SelectItem key={style} value={style}>{style}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
            data-testid="send-quote-request-btn"
          >
            {submitting ? (
              '...'
            ) : (
              <>
                <Send size={16} className="mr-2" />
                {t('quotes.sendRequest')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestQuoteButton;
